const isNumber = (value: unknown): value is number => {
  return typeof value === 'number' && !isNaN(value)
}

export type NumberFormatOptions = Intl.NumberFormatOptions & {
  prefix?: 'plus' | 'minus'
  fallback?: string
  fractionDigits?: number
}

/* One source of truth for number formatting */
const baseFormat = (number?: number | null, options: NumberFormatOptions = {}) => {
  const { fallback = 'N/A', prefix, ...rest } = options

  if (!isNumber(number)) return fallback ?? null
  const result = new Intl.NumberFormat('en-US', rest).format(number as number)
  if (prefix === 'plus') return `+${result}`
  if (prefix === 'minus') return `-${result}`
  return result
}

export const formatInt = (value?: number | null, options: NumberFormatOptions = {}) => {
  const { fractionDigits = 0, ...rest } = options
  const _options = {
    maximumFractionDigits: fractionDigits,
    minimumFractionDigits: fractionDigits,
    ...rest,
  }
  return baseFormat(value, _options)
}

export const formatFloat = (value?: number | null, options: NumberFormatOptions = {}) => {
  const { fractionDigits = 2, ...rest } = options
  const _options = {
    fractionDigits,
    ...rest,
  }
  return formatInt(value, _options)
}

export const formatCurrency = (value?: number | null, options: NumberFormatOptions = {}) => {
  return formatInt(value, {
    currency: 'USD',
    ...options,
    style: 'currency',
  })
}

export const formatCurrencyFloat = (value?: number | null, options: NumberFormatOptions = {}) => {
  return formatFloat(value, {
    currency: 'USD',
    ...options,
    style: 'currency',
  })
}

export const formatIntPercentage = (value?: number | null, options: NumberFormatOptions = {}) => {
  return formatInt(value, { ...options, style: 'percent' })
}

export const formatFloatPercentage = (value?: number | null, options: NumberFormatOptions = {}) => {
  return formatFloat(value, { ...options, style: 'percent' })
}

const roundDown = (value: number, fractionDigits: number): number => {
  const factor = Math.pow(10, fractionDigits)
  return Math.floor(value * factor) / factor
}

export const formatCurrencyLargeCompact = (value?: number | null, options: NumberFormatOptions = {}) => {
  if (!isNumber(value)) return formatCurrency(value, options)

  const oneBillion = 1_000_000_000
  let minimumFractionDigits = 0
  let maximumFractionDigits = 0

  let roundedValue = value
  if (value >= oneBillion) {
    minimumFractionDigits = 1
    maximumFractionDigits = 2
  }

  roundedValue = roundDown(value, maximumFractionDigits)
  return formatCurrency(roundedValue, {
    ...options,
    notation: 'compact',
    minimumFractionDigits,
    maximumFractionDigits,
  })
}
